// Tipos de autenticação e usuário

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser?: boolean;
  // Operador da plataforma: lista e cria empresas, sem acesso aos dados das lojas.
  can_create_companies?: boolean;
  permissions?: string[];
  groups?: string[];
  date_joined: string;
  last_login: string | null;
  avatar?: string;
  company?: string;
  company_name?: string;
  company_role?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  remember?: boolean;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: User;
  expires_in?: number;
  remember_me?: boolean;
  token_expires_at?: {
    access: number;
    refresh: number;
  };
}

export interface ChangePasswordData {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface LoginAttempt {
  id: string;
  user: string;
  email: string;
  ip_address: string;
  user_agent: string;
  success: boolean;
  failure_reason?: string;
  timestamp: string;
}

// Estado de autenticação
export interface AuthState {
  // Estado do usuário
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Tokens
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiry: number | null;
  rememberMe: boolean;
  
  // Ações
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  register?: (data: any) => Promise<User>;
  updateProfile: (data: UpdateProfileData) => Promise<User>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  refreshAuth: () => Promise<string>;
  checkAuth: () => Promise<boolean>;
  
  // Utilitários
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  getDisplayName: () => string;
  getInitials: () => string;
  
  // Estado interno
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setTokens: (access: string, refresh: string) => void;
  clearAuth: () => void;
  initialize: () => Promise<void>;
}

// Props de componentes de autenticação
export interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  permissions?: string[];
  roles?: string[];
  requireAuth?: boolean;
  redirectTo?: string;
}

export interface LoginFormProps {
  onSubmit: (credentials: LoginCredentials) => Promise<void>;
  loading?: boolean;
  error?: string;
  className?: string;
}

export interface UserMenuProps {
  user: User;
  onLogout: () => void;
  className?: string;
}

// Tipos de contexto e hooks
export interface AuthContextType extends AuthState {}

export interface UseAuthReturn extends AuthState {
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  isLoggedIn: boolean;
}

export interface UsePermissionsReturn {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: boolean;
  isSuperuser: boolean;
  isStaff: boolean;
  permissions: string[];
  roles: string[];
}

// Tipos de rota
export interface RoutePermission {
  path: string;
  permissions?: string[];
  roles?: string[];
  requireAuth: boolean;
}

// Tipos de configuração de auth
export interface AuthConfig {
  loginPath: string;
  dashboardPath: string;
  tokenKey: string;
  refreshTokenKey: string;
  rememberMeKey: string;
  tokenExpiry: number;
  refreshBeforeExpiry: number;
  maxRetries: number;
}

// Enums para permissões comuns
export enum Permission {
  // Produtos
  PRODUCT_VIEW = 'product.view',
  PRODUCT_CREATE = 'product.create',
  PRODUCT_EDIT = 'product.edit',
  PRODUCT_DELETE = 'product.delete',
  PRODUCT_SYNC = 'product.sync',
  
  // Pedidos
  ORDER_VIEW = 'order.view',
  ORDER_EDIT = 'order.edit',
  ORDER_DELETE = 'order.delete',
  ORDER_SYNC = 'order.sync',
  
  // Sincronização
  SYNC_VIEW = 'sync.view',
  SYNC_EXECUTE = 'sync.execute',
  SYNC_CONFIGURE = 'sync.configure',
  
  // Mapeamento
  MAPPING_VIEW = 'mapping.view',
  MAPPING_CREATE = 'mapping.create',
  MAPPING_EDIT = 'mapping.edit',
  MAPPING_DELETE = 'mapping.delete',
  
  // Configurações
  SETTINGS_VIEW = 'settings.view',
  SETTINGS_EDIT = 'settings.edit',
  
  // Admin
  ADMIN_ACCESS = 'admin.access',
  USER_MANAGE = 'user.manage',
  SYSTEM_CONFIG = 'system.config',
  
  // Logs e auditoria
  LOGS_VIEW = 'logs.view',
  AUDIT_VIEW = 'audit.view',
}

export enum Role {
  ADMIN = 'admin',
  MANAGER = 'manager',
  OPERATOR = 'operator',
  VIEWER = 'viewer',
}

// Tipos utilitários
export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface AuthError {
  code: string;
  message: string;
  details?: any;
}